import { Injectable, inject, signal } from "@angular/core";
import { WINDOW_TOKEN } from "@kno2/core/browser";

export enum PopupCallbackCode {
    IdProofing = "idproofing",
    PccSso = "pccsso"
}

export interface PopupCallbackConfig<T = unknown> {
    messageType: PopupCallbackCode;
    errorMessages: {
        popupBlocked: string;
        popupClosed: string;
    };
    onSuccess: (data: T) => Promise<void>;
    onError: (error: unknown) => void;
}

@Injectable()
export class PopupCallbackHandler {
    private readonly window = inject(WINDOW_TOKEN);

    private static readonly POPUP_CHECK_INTERVAL_MS = 1000;

    private popupWindow: Window | null = null;
    private messageListener: ((event: MessageEvent) => void) | null = null;
    private popupCheckInterval: number | null = null;
    private currentConfig: PopupCallbackConfig | null = null;

    public readonly isWaiting = signal(false);

    public async openPopup<T>(authUrl: string, config: PopupCallbackConfig<T>): Promise<void> {
        this.popupWindow = this.window.open(authUrl);

        if (!this.popupWindow) {
            config.onError(new Error(config.errorMessages.popupBlocked));
            return;
        }

        this.currentConfig = config;
        this.isWaiting.set(true);
        this.setupMessageListener();
        this.setupPopupMonitoring();
    }

    public destroy(): void {
        this.cleanup();
    }

    private setupMessageListener(): void {
        if (!this.currentConfig) return;

        this.messageListener = (event: MessageEvent) => {
            if (event.origin !== this.window.location.origin) {
                return;
            }

            if (event.data?.type === this.currentConfig.messageType) {
                this.handleCallback(event.data);
            }
        };

        this.window.addEventListener("message", this.messageListener);
    }

    private setupPopupMonitoring(): void {
        if (!this.currentConfig) return;

        this.popupCheckInterval = this.window.setInterval(() => {
            if (this.popupWindow?.closed) {
                this.currentConfig.onError(new Error(this.currentConfig.errorMessages.popupClosed));
                this.cleanup();
            }
        }, PopupCallbackHandler.POPUP_CHECK_INTERVAL_MS);
    }

    private async handleCallback(data: unknown): Promise<void> {
        if (!this.currentConfig) return;

        const config = this.currentConfig;
        this.cleanup();

        try {
            await config.onSuccess(data);
        } catch (error) {
            config.onError(error);
        }
    }

    private cleanup(): void {
        this.isWaiting.set(false);

        if (this.messageListener) {
            this.window.removeEventListener("message", this.messageListener);
            this.messageListener = null;
        }

        if (this.popupCheckInterval) {
            clearInterval(this.popupCheckInterval);
            this.popupCheckInterval = null;
        }

        if (this.popupWindow && !this.popupWindow.closed) {
            this.popupWindow.close();
        }

        this.popupWindow = null;
        this.currentConfig = null;
    }
}
